"use client";

import type React from "react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Calendar, Truck, CreditCard, Package } from "lucide-react";
import {
  OrderStatus,
  OrderPaymentStatus,
  type OrderOutput,
  type CustomersOutput,
  orderStatusTranslation,
  orderPaymentStatusTranslation,
  orderDeliveryTypeTransalation,
  type TagsOutput,
  OrderDeliveryType,
} from "@/interfaces";
import { format } from "date-fns";
import { Separator } from "@/components/ui/separator";
import { numberToMoney } from "@/util/moneyConversion";

interface OrderCardProps {
  order: OrderOutput;
  onDelete: () => void;
  className?: string;
}

const ORDER_STATUS_BADGE_COLORS: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: "bg-yellow-100 text-yellow-800 border-yellow-200",
  [OrderStatus.SCHEDULED]: "bg-blue-100 text-blue-800 border-blue-200",
  [OrderStatus.IN_PREPARATION]: "bg-orange-100 text-orange-800 border-orange-200",
  [OrderStatus.PREPARED]: "bg-teal-100 text-teal-800 border-teal-200",
  [OrderStatus.DONE]: "bg-green-100 text-green-800 border-green-200",
  [OrderStatus.CANCELED]: "bg-red-100 text-red-800 border-red-200",
};

const ORDER_STATUS_BORDER_COLORS: Record<OrderStatus, string> = {
  [OrderStatus.PENDING]: "#eab308",
  [OrderStatus.SCHEDULED]: "#3b82f6",
  [OrderStatus.IN_PREPARATION]: "#f97316",
  [OrderStatus.PREPARED]: "#14b8a6",
  [OrderStatus.DONE]: "#22c55e",
  [OrderStatus.CANCELED]: "#ef4444",
};

const PAYMENT_STATUS_BADGE_COLORS: Record<OrderPaymentStatus, string> = {
  [OrderPaymentStatus.PAID]: "bg-green-100 text-green-800 border-green-200",
  [OrderPaymentStatus.PENDING]: "bg-yellow-100 text-yellow-800 border-yellow-200",
  [OrderPaymentStatus.PARTIALLY_PAID]:
    "bg-orange-100 text-orange-800 border-orange-200",
};

function getCustomerDisplayName(order: OrderOutput) {
  if (order.customer) {
    return (order.customer as CustomersOutput)?.name;
  }

  return `Pedido #${order.id.substring(0, 8)}`;
}

function getPreparationDateLabel(preparationDate: Date) {
  const preparationDateValue = new Date(preparationDate);
  const prefix =
    preparationDateValue > new Date() ? "Preparação em " : "Preparado em ";

  return `${prefix}${format(preparationDateValue, "dd/MM/yyyy")}`;
}

export function OrderCard({
  order,
  onDelete,
  className = "",
}: Readonly<OrderCardProps>) {
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  const visibleProducts = order.products.slice(0, 3);
  const hiddenProductsCount = order.products.length - visibleProducts.length;

  return (
    <Card
      className={`w-full min-w-80 border-l-4 cursor-pointer hover:shadow-md transition-all duration-200 flex flex-col ${className}`}
      style={{
        borderLeftColor: ORDER_STATUS_BORDER_COLORS[order.status],
      }}
    >
      <CardHeader className="p-4 pb-2">
        <div className="flex justify-between items-start gap-2">
          <div className="space-y-1">
            <CardTitle className="text-base sm:text-lg font-semibold line-clamp-1">
              {getCustomerDisplayName(order)}
            </CardTitle>
            <CardDescription className="text-xs flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {getPreparationDateLabel(order.preparationDate)}
            </CardDescription>
          </div>
          <Badge
            className={`${ORDER_STATUS_BADGE_COLORS[order.status]} border px-2 py-0.5 text-xs font-medium shrink-0`}
          >
            {orderStatusTranslation[order.status]}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-4 pt-0 flex flex-col gap-3 flex-1">
        {order.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {(order.tags as TagsOutput[]).map((tag) => (
              <Badge
                key={tag.id}
                style={{
                  backgroundColor: tag.styling?.primaryColor,
                  color: tag.styling?.fontColor,
                  border: `1px solid ${tag.styling?.secondaryColor}`,
                }}
                className="text-[10px] px-1.5 py-0 rounded"
                variant="outline"
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-2 text-sm mt-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Truck className="h-3.5 w-3.5" />
              <span>Entrega:</span>
            </div>
            <span className="font-medium">
              {orderDeliveryTypeTransalation[order.delivery.deliveryType]}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <CreditCard className="h-3.5 w-3.5" />
              <span>Pagamento:</span>
            </div>
            <Badge
              className={`${PAYMENT_STATUS_BADGE_COLORS[order.paymentStatus]} border px-1.5 py-0 text-[10px] font-medium`}
            >
              {orderPaymentStatusTranslation[order.paymentStatus]}
            </Badge>
          </div>

          {order.delivery.deliveryType === OrderDeliveryType.DELIVERY && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <Calendar className="h-3.5 w-3.5" />
                <span>Entrega em:</span>
              </div>

              <span className="font-medium">
                {format(new Date(order.delivery.deliveryAt!), "dd/MM - HH:mm")}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <CreditCard className="h-3.5 w-3.5" />
              <span>Valor:</span>
            </div>
            <span className="font-bold text-green-700 dark:text-green-500">
              {numberToMoney(order.totalAmount)}
            </span>
          </div>
        </div>

        <div className="mt-auto pt-3">
          <Separator className="mb-3" />
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                <Package className="h-3.5 w-3.5" />
                <span>Produtos:</span>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-0.5 max-w-[90%]">
                {visibleProducts.map((p) => (
                  <p key={p.productId} className="line-clamp-1">
                    {p.name} <span className="font-medium">x {p.quantity}</span>
                  </p>
                ))}
                {hiddenProductsCount > 0 && (
                  <p className="text-xs text-gray-500 italic">
                    + {hiddenProductsCount} mais
                  </p>
                )}
              </div>
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Excluir pedido</span>
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
